/** 대메뉴 안의 좌측 소메뉴 */
export interface PortalSideItem {
  label: string
  to: string
  /** 이 경로와 그 하위를 현재 메뉴로 본다. 없으면 to */
  match?: string[]
  /** true면 링크로 연결하지 않고 그룹 제목처럼만 보인다 (하위 메뉴로만 진입) */
  disabled?: boolean
  /** 관리자(슈퍼바이저) 세션에서만 메뉴에 보인다 */
  adminOnly?: boolean
  children?: PortalSideItem[]
}

export interface PortalSection {
  id: 'service' | 'board' | 'support'
  label: string
  to: string
  description: string
  tone: 'work' | 'info' | 'board' | 'support'
  match: string[]
  items: PortalSideItem[]
}

export const PORTAL_SECTIONS: PortalSection[] = [
  {
    id: 'service',
    label: '서비스',
    to: '/wage',
    description: '장기요양기관 운영에 필요한 임금과 회계 업무를 한곳에서 관리하세요.',
    tone: 'work',
    match: ['/wage', '/facility', '/visiting-care', '/accounting'],
    items: [
      {
        /* 임금관리는 사업구분 하위 메뉴로만 들어간다 */
        label: '임금관리',
        to: '/wage',
        match: ['/wage'],
        disabled: true,
        children: [
          { label: '요양원·주간보호', to: '/facility', match: ['/facility'] },
          { label: '방문요양·목욕', to: '/visiting-care', match: ['/visiting-care'] },
        ],
      },
      { label: '재무회계', to: '/accounting', match: ['/accounting'] },
    ],
  },
  {
    id: 'board',
    label: '게시판',
    to: '/announcements',
    description: '장기요양지원센터의 새로운 소식을 확인하세요.',
    tone: 'board',
    match: ['/announcements', '/newsletters', '/notice', '/info', '/faq'],
    items: [
      { label: '공지사항', to: '/announcements', match: ['/announcements'] },
      { label: 'FAQ', to: '/faq', match: ['/faq'] },
      { label: '뉴스레터', to: '/newsletters', match: ['/newsletters'] },
      { label: '고시 검색', to: '/notice', match: ['/notice'] },
      { label: '정보마당', to: '/info', match: ['/info'], adminOnly: true },
    ],
  },
  {
    id: 'support',
    label: '상담',
    to: '/support',
    description: '기관 운영에 관한 안내와 상담을 확인하세요.',
    tone: 'support',
    match: ['/support', '/consult-board'],
    items: [
      { label: '상담 안내', to: '/support', match: ['/support'] },
      { label: '상담 신청', to: '/consult-board/write', match: ['/consult-board'] },
    ],
  },
]

export function filterAdminOnlyNav<T extends { adminOnly?: boolean; children?: T[] }>(
  items: T[],
  isAdmin: boolean,
): T[] {
  return items.flatMap((item) => {
    if (item.adminOnly && !isAdmin) return []
    const children = item.children ? filterAdminOnlyNav(item.children, isAdmin) : undefined
    return [{ ...item, ...(item.children ? { children } : {}) }]
  })
}

function pathHits(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`)
}

export function portalSectionForPath(path: string): PortalSection | null {
  return PORTAL_SECTIONS.find((section) => section.match.some((prefix) => pathHits(path, prefix))) ?? null
}

function flatten(items: PortalSideItem[]): PortalSideItem[] {
  const out: PortalSideItem[] = []
  items.forEach((item) => {
    out.push(item)
    if (item.children?.length) out.push(...flatten(item.children))
  })
  return out
}

function matchLength(item: PortalSideItem): number {
  const keys = item.match?.length ? item.match : [item.to]
  return Math.max(...keys.map((key) => key.length))
}

/** 더 구체적인 경로가 현재 메뉴가 된다 */
export function sideItemCurrent(path: string, items: PortalSideItem[]): PortalSideItem | null {
  const hits = flatten(items).filter((item) => {
    const keys = item.match?.length ? item.match : [item.to]
    return keys.some((key) => pathHits(path, key))
  })
  hits.sort((a, b) => matchLength(b) - matchLength(a))
  return hits[0] ?? null
}
